import * as yup from 'yup';
import { uniq } from 'ramda';
import type { AnyObjectSchema } from 'yup';
import type { TranslationPath } from 'c-translation';
import { campaignFormMaxDays } from 'c-main/Lib';
import type { NewCampaignFormSchema } from 'c-main/Types';
import { CampaignFormNames } from 'c-main/Types';
import { stepOneSchema } from './types';

/**
 * The new-campaign wizard's steps, what each one must have before "Next" (or "Create Campaign")
 * is enabled, and the reasons shown under the button while it isn't.
 */

export enum NewCampaignStep {
    Details,
    ChooseDisplays,
    Schedule,
    Summary,
}

export const newCampaignStepCount = 4;

/** A line item's live days: blank means "the whole campaign", otherwise a whole 1..maxDays. */
export const isValidLineItemDays = (days: unknown, maxDays: number) => {
    if (days == null || days === '') return true;

    const value = Number(days);
    return Number.isInteger(value) && value >= 1 && value <= maxDays;
};

export const scheduleSchema = () =>
    yup.object({
        [CampaignFormNames.lineItemDays]: yup
            .mixed()
            .test('line-item-days', '', function lineItemDays(days) {
                const values = this.parent as NewCampaignFormSchema;
                const maxDays = campaignFormMaxDays(values);
                const errors = (values.line_items ?? [])
                    .filter(id => !isValidLineItemDays(days?.[id], maxDays))
                    // one error per row, at the row's own field, so the row shows it
                    .map(id =>
                        this.createError({ path: `${CampaignFormNames.lineItemDays}.${id}` }),
                    );

                return errors.length === 0 ? true : new yup.ValidationError(errors);
            }),
    }) as AnyObjectSchema;

/** What the current step must pass. Summary re-checks everything, as it submits. */
export const newCampaignStepSchema = (
    step: NewCampaignStep,
    detailsOverrides?: Record<string, yup.AnySchema>,
): AnyObjectSchema => {
    switch (step) {
        case NewCampaignStep.ChooseDisplays:
            // displays are optional: a campaign can be created empty and filled in later
            return yup.object({}) as AnyObjectSchema;
        case NewCampaignStep.Schedule:
            return scheduleSchema();
        case NewCampaignStep.Summary:
            return stepOneSchema(detailsOverrides).concat(scheduleSchema());
        case NewCampaignStep.Details:
        default:
            return stepOneSchema(detailsOverrides);
    }
};

export type StepBlocker = {
    path: TranslationPath;
    params?: Record<string, unknown>;
};

const blockerPaths: Record<string, TranslationPath> = {
    [CampaignFormNames.name]: 'Modules.Main.Campaigns.Create.Blockers.name',
    [CampaignFormNames.reference]: 'Modules.Main.Campaigns.Create.Blockers.reference',
    [CampaignFormNames.start]: 'Modules.Main.Campaigns.Create.Blockers.dates',
    [CampaignFormNames.end]: 'Modules.Main.Campaigns.Create.Blockers.dates',
    [CampaignFormNames.buyer]: 'Modules.Main.Campaigns.Create.Blockers.buyer',
    [CampaignFormNames.client_name]: 'Modules.Main.Campaigns.Create.Blockers.client',
    [CampaignFormNames.lineItemDays]: 'Modules.Main.Campaigns.Create.Blockers.lineItemDays',
};

const otherBlocker: TranslationPath = 'Modules.Main.Campaigns.Create.Blockers.other';

/**
 * Why `schema` refuses `values`, one reason per field in the order the schema lists them, for the
 * text under a disabled "Next". Empty when the values pass.
 */
export const stepBlockers = (
    schema: AnyObjectSchema,
    values: Partial<NewCampaignFormSchema>,
): StepBlocker[] => {
    try {
        schema.validateSync(values ?? {}, { abortEarly: false });
        return [];
    } catch (e) {
        if (!yup.ValidationError.isError(e)) throw e;

        const errors = e.inner.length > 0 ? e.inner : [e];
        const paths = errors.map(err => {
            const field = (err.path ?? '').split(/[.[]/)[0];
            return blockerPaths[field] ?? otherBlocker;
        });

        return uniq(paths).map(path =>
            path === blockerPaths[CampaignFormNames.lineItemDays]
                ? { path, params: { max: campaignFormMaxDays(values ?? {}) } }
                : { path },
        );
    }
};
